/**
 * constraints.js - Section Template Constraint System
 * Location: /app/frontend/src/admin/page_builder/constraints/
 * 
 * Provides validation and enforcement of template-level constraints:
 * - allowedLayouts: Which layout presets are valid for this template
 * - allowedBackgrounds: Which background types are valid
 * - allowedCardVariants: Which card variants are valid
 * - maxBlocks: Maximum number of this block type per page
 * 
 * Key functions:
 * - validateSectionConfig: Check if a section config is valid
 * - coerceSectionConfig: Auto-fix invalid config to defaults
 * - isLayoutAllowed: Check if a layout preset is allowed
 * - isBackgroundAllowed: Check if a background type is allowed
 * - isCardVariantAllowed: Check if a card variant is allowed
 */

import { LayoutPreset } from '../layout/presets';
import { BackgroundType } from '../layout/backgrounds';
import { CardVariant } from '../cards/variants';
import { getTemplateById } from '../templates/registry';

/**
 * Default constraints if template doesn't specify
 */
export const DEFAULT_CONSTRAINTS = {
  allowedLayouts: Object.values(LayoutPreset),
  allowedBackgrounds: Object.values(BackgroundType),
  allowedCardVariants: Object.values(CardVariant),
  maxBlocks: null, // null = unlimited
};

/**
 * Get constraints for a template
 * @param {string} templateId - Template ID
 * @returns {Object} Constraints object
 */
export function getTemplateConstraints(templateId) {
  const template = getTemplateById(templateId);
  if (!template) {
    return DEFAULT_CONSTRAINTS;
  }
  
  return {
    allowedLayouts: template.allowedLayouts || DEFAULT_CONSTRAINTS.allowedLayouts,
    allowedBackgrounds: template.allowedBackgrounds || DEFAULT_CONSTRAINTS.allowedBackgrounds,
    allowedCardVariants: template.allowedCardVariants || DEFAULT_CONSTRAINTS.allowedCardVariants,
    maxBlocks: template.maxBlocks ?? DEFAULT_CONSTRAINTS.maxBlocks,
  };
}

/**
 * Check if a layout preset is allowed for a template
 * @param {string} templateId - Template ID
 * @param {string} layoutPreset - Layout preset value
 * @returns {boolean}
 */
export function isLayoutAllowed(templateId, layoutPreset) {
  const constraints = getTemplateConstraints(templateId);
  return constraints.allowedLayouts.includes(layoutPreset);
}

/**
 * Check if a background type is allowed for a template
 * @param {string} templateId - Template ID
 * @param {string} backgroundType - Background type value
 * @returns {boolean}
 */
export function isBackgroundAllowed(templateId, backgroundType) {
  const constraints = getTemplateConstraints(templateId);
  return constraints.allowedBackgrounds.includes(backgroundType);
}

/**
 * Check if a card variant is allowed for a template
 * @param {string} templateId - Template ID
 * @param {string} cardVariant - Card variant value
 * @returns {boolean}
 */
export function isCardVariantAllowed(templateId, cardVariant) {
  const constraints = getTemplateConstraints(templateId);
  return constraints.allowedCardVariants.includes(cardVariant);
}

/**
 * Validate a section configuration against its template constraints
 * @param {Object} section - Section configuration
 * @returns {Object} Validation result { valid: boolean, errors: string[] }
 */
export function validateSectionConfig(section) {
  const errors = [];
  const templateId = section?._templateId || section?.type;
  const constraints = getTemplateConstraints(templateId);
  const template = getTemplateById(templateId);
  
  // Check layout
  const layoutPreset = section?.layout?.preset || LayoutPreset.SINGLE;
  if (!isLayoutAllowed(templateId, layoutPreset)) {
    errors.push(`Layout "${layoutPreset}" is not allowed for this section. Allowed: ${constraints.allowedLayouts.join(', ')}`);
  }
  
  // Check background
  const backgroundType = section?.background?.type || BackgroundType.NONE;
  if (!isBackgroundAllowed(templateId, backgroundType)) {
    errors.push(`Background "${backgroundType}" is not allowed for this section. Allowed: ${constraints.allowedBackgrounds.join(', ')}`);
  }
  
  // Check card variant (if applicable)
  if (section?.cardVariant && template?.allowedCardVariants) {
    if (!isCardVariantAllowed(templateId, section.cardVariant)) {
      errors.push(`Card variant "${section.cardVariant}" is not allowed for this section. Allowed: ${constraints.allowedCardVariants.join(', ')}`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Coerce a section configuration to valid defaults if constraints are violated
 * @param {Object} section - Section configuration
 * @returns {Object} Coerced section configuration
 */
export function coerceSectionConfig(section) {
  if (!section) return section;
  
  const templateId = section._templateId || section.type;
  const template = getTemplateById(templateId);
  const constraints = getTemplateConstraints(templateId);
  
  const coerced = { ...section };
  let wasCoerced = false;
  
  // Coerce layout
  const currentLayout = section?.layout?.preset || LayoutPreset.SINGLE;
  if (!isLayoutAllowed(templateId, currentLayout)) {
    const defaultLayout = template?.defaultLayout?.preset || constraints.allowedLayouts[0] || LayoutPreset.SINGLE;
    coerced.layout = { ...coerced.layout, preset: defaultLayout };
    wasCoerced = true;
    console.warn(`[Constraints] Coerced layout from "${currentLayout}" to "${defaultLayout}" for section ${section.id}`);
  }
  
  // Coerce background
  const currentBackground = section?.background?.type || BackgroundType.NONE;
  if (!isBackgroundAllowed(templateId, currentBackground)) {
    const defaultBackground = template?.defaultBackground?.type || constraints.allowedBackgrounds[0] || BackgroundType.NONE;
    coerced.background = { 
      type: defaultBackground,
      config: null,
      overlayOpacity: 0,
    };
    wasCoerced = true;
    console.warn(`[Constraints] Coerced background from "${currentBackground}" to "${defaultBackground}" for section ${section.id}`);
  }
  
  // Coerce card variant
  if (section?.cardVariant && template?.allowedCardVariants) {
    if (!isCardVariantAllowed(templateId, section.cardVariant)) {
      const defaultCardVariant = template?.cardVariant || constraints.allowedCardVariants[0] || CardVariant.DISCOVERY;
      coerced.cardVariant = defaultCardVariant;
      wasCoerced = true;
      console.warn(`[Constraints] Coerced cardVariant from "${section.cardVariant}" to "${defaultCardVariant}" for section ${section.id}`);
    }
  }
  
  // Mark if coerced for UI feedback
  if (wasCoerced) {
    coerced._wasCoerced = true;
  }
  
  return coerced;
}

/**
 * Check if adding another block of this type would violate maxBlocks constraint
 * @param {string} templateId - Template ID
 * @param {Array} existingBlocks - Current page blocks
 * @returns {Object} { allowed: boolean, currentCount: number, maxAllowed: number|null }
 */
export function canAddBlock(templateId, existingBlocks = []) {
  const constraints = getTemplateConstraints(templateId);
  
  // If no maxBlocks constraint, always allow
  if (constraints.maxBlocks === null || constraints.maxBlocks === undefined) {
    return { allowed: true, currentCount: 0, maxAllowed: null };
  }
  
  // Count existing blocks of this type
  const currentCount = existingBlocks.filter(
    b => b._templateId === templateId || b.type === templateId
  ).length;
  
  return {
    allowed: currentCount < constraints.maxBlocks,
    currentCount,
    maxAllowed: constraints.maxBlocks,
  };
}

/**
 * Validate an entire page configuration
 * @param {Array} blocks - Array of section blocks
 * @returns {Object} { valid: boolean, errors: Array<{blockId, errors}>, blockCountErrors: string[] }
 */
export function validatePageConfig(blocks = []) {
  const sectionErrors = [];
  const blockCountErrors = [];
  
  // Validate each section
  for (const block of blocks) {
    const validation = validateSectionConfig(block);
    if (!validation.valid) {
      sectionErrors.push({
        blockId: block.id,
        errors: validation.errors,
      });
    }
  }
  
  // Check maxBlocks constraints
  const blockCounts = {};
  for (const block of blocks) {
    const templateId = block._templateId || block.type;
    blockCounts[templateId] = (blockCounts[templateId] || 0) + 1;
  }
  
  for (const [templateId, count] of Object.entries(blockCounts)) {
    const constraints = getTemplateConstraints(templateId);
    if (constraints.maxBlocks !== null && count > constraints.maxBlocks) {
      blockCountErrors.push(
        `Too many "${templateId}" blocks: ${count} (max: ${constraints.maxBlocks})`
      );
    }
  }
  
  return {
    valid: sectionErrors.length === 0 && blockCountErrors.length === 0,
    sectionErrors,
    blockCountErrors,
  };
}

/**
 * Coerce an entire page configuration to valid defaults
 * @param {Array} blocks - Array of section blocks
 * @returns {Array} Coerced blocks array
 */
export function coercePageConfig(blocks = []) {
  return blocks.map(block => coerceSectionConfig(block));
}

/**
 * Get UI-friendly constraint info for a template
 * @param {string} templateId - Template ID
 * @returns {Object} Constraint info for UI display
 */
export function getConstraintInfo(templateId) {
  const constraints = getTemplateConstraints(templateId);
  const template = getTemplateById(templateId);
  
  return {
    templateId,
    templateName: template?.name || templateId,
    allowedLayouts: constraints.allowedLayouts.map(l => ({
      value: l,
      allowed: true,
    })),
    disallowedLayouts: Object.values(LayoutPreset)
      .filter(l => !constraints.allowedLayouts.includes(l))
      .map(l => ({ value: l, allowed: false })),
    allowedBackgrounds: constraints.allowedBackgrounds.map(b => ({
      value: b,
      allowed: true,
    })),
    disallowedBackgrounds: Object.values(BackgroundType)
      .filter(b => !constraints.allowedBackgrounds.includes(b))
      .map(b => ({ value: b, allowed: false })),
    allowedCardVariants: constraints.allowedCardVariants.map(c => ({
      value: c,
      allowed: true,
    })),
    maxBlocks: constraints.maxBlocks,
    hasConstraints: 
      constraints.allowedLayouts.length < Object.values(LayoutPreset).length ||
      constraints.allowedBackgrounds.length < Object.values(BackgroundType).length ||
      constraints.maxBlocks !== null,
  };
}

export default {
  DEFAULT_CONSTRAINTS,
  getTemplateConstraints,
  isLayoutAllowed,
  isBackgroundAllowed,
  isCardVariantAllowed,
  validateSectionConfig,
  coerceSectionConfig,
  canAddBlock,
  validatePageConfig,
  coercePageConfig,
  getConstraintInfo,
};
